import type { TeactNode } from '../../lib/teact/teact';
import React, { memo, useEffect } from '../../lib/teact/teact';
import { getActions, getGlobal, withGlobal } from '../../global';

import { IS_LEGENDS_WALLET, WRONG_ATTEMPTS_BEFORE_LOG_OUT_SUGGESTION } from '../../config';
import { getIsFaceIdAvailable } from '../../util/biometrics';
import buildClassName from '../../util/buildClassName';
import { vibrateOnError } from '../../util/haptics';
import { disableSwipeToClose, enableSwipeToClose } from '../../util/modalSwipeManager';
import { SWIPE_DISABLED_CLASS_NAME } from '../../util/swipeController';
import { IS_IOS } from '../../util/windowEnvironment';

import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';
import { useMatchCount } from '../../hooks/useMatchCount';
import { usePrevDuringAnimationSimple } from '../../hooks/usePrevDuringAnimationSimple';
import usePrevious from '../../hooks/usePrevious';

import PinPadButton from './PinPadButton';
import Transition from './Transition';

import styles from './PinPad.module.scss';

interface OwnProps {
  isActive?: boolean;
  isLoading?: boolean;
  title: string;
  type?: 'error' | 'success';
  value: string;
  length?: number;
  resetStateDelayMs?: number;
  className?: string;
  isMinified?: boolean;
  isPasscodeScreen?: boolean;
  isInAppLock?: boolean;
  topContent?: TeactJsx;
  footer?: TeactNode;
  onBiometricsClick?: NoneToVoidFunction;
  onChange: (value: string) => void;
  onClearError?: NoneToVoidFunction;
  onSubmit: (pin: string) => void;
  onLogOutClick?: NoneToVoidFunction;
}

interface StateProps {
  isPinAccepted?: boolean;
};

const DEFAULT_PIN_LENGTH = 4;
const RESET_STATE_DELAY_MS = 1500;
const KEY_LETTERS = ['+', '', 'ABC', 'DEF', 'GHI', 'JKL', 'MNO', 'PQRS', 'TUV', 'WXYZ'];
// Should match duration from `--layer-transition` CSS variable
const STATE_ANIMATION_DURATION_MS = IS_IOS ? 650 : 300;

export function PinPad({
  isActive,
  isLoading,
  title,
  type,
  value,
  resetStateDelayMs = RESET_STATE_DELAY_MS,
  length = DEFAULT_PIN_LENGTH,
  isPinAccepted,
  className,
  isMinified,
  isPasscodeScreen,
  isInAppLock,
  topContent,
  footer,
  onBiometricsClick,
  onChange,
  onClearError,
  onSubmit,
  onLogOutClick,
}: OwnProps & StateProps) {
  const { clearIsPinAccepted } = getActions();
  const lang = useLang();

  const isFaceId = getIsFaceIdAvailable();
  const canRenderBackspace = value.length > 0;
  const isSuccess = type === 'success' || isPinAccepted;
  const isPending = isSuccess || isLoading;
  const isSuccessDuringAnimation = usePrevDuringAnimationSimple(isSuccess, STATE_ANIMATION_DURATION_MS);
  const prevIsPinAccepted = usePrevious(isPinAccepted);
  const arePinButtonsDisabled = isPending
    || (value.length === length && type !== 'error'); // Allow pincode entry in case of an error

  const titleClassName = buildClassName(
    styles.title,
    type === 'error' && styles.error,
    (isSuccess || isSuccessDuringAnimation) && styles.success,
  );

  const shouldSuggestLogout = useMatchCount(type === 'error', WRONG_ATTEMPTS_BEFORE_LOG_OUT_SUGGESTION);

  useEffect(() => {
    if (prevIsPinAccepted && !isPinAccepted && length === value.length) {
      onChange('');
    }
  }, [isPinAccepted, length, onChange, prevIsPinAccepted, value.length]);

  useEffect(() => clearIsPinAccepted, []);

  useEffect(() => {
    if (type !== 'error') return undefined;

    const timeoutId = window.setTimeout(() => {
      if (value.length === length) {
        onChange('');
      }
      onClearError?.();
    }, resetStateDelayMs);
    vibrateOnError();

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [length, onChange, onClearError, resetStateDelayMs, type, value.length]);

  useEffect(() => {
    if (!isActive) return undefined;

    disableSwipeToClose();

    return () => {
      enableSwipeToClose();
    };
  }, [isActive]);

  const handleClick = useLastCallback((char: string) => {
    if (value.length === length || value.length === 0) {
      onClearError?.();
    }

    if (value.length === length) {
      onChange(char);

      return;
    }

    const newValue = `${value}${char}`;
    onChange(newValue);

    if (newValue.length === length) {
      onSubmit(newValue);
    }
  });

  const handleBackspaceClick = useLastCallback(() => {
    onClearError?.();
    if (!value.length) return;

    onChange(value.slice(0, -1));
  });

  useEffect(() => {
    if (!isPasscodeScreen || !isActive) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (getGlobal().isAppLockActive && !isInAppLock) return;
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || isPending
        || (event.target as HTMLElement)?.closest?.('input, textarea, [contenteditable="true"]')) return;
      if (/^[0-9]$/.test(event.key) && !arePinButtonsDisabled) {
        event.preventDefault();
        handleClick(event.key);
      } else if (event.key === 'Backspace') {
        event.preventDefault();
        handleBackspaceClick();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [arePinButtonsDisabled, handleBackspaceClick, handleClick, isActive, isInAppLock, isPasscodeScreen, isPending]);

  function renderNumber(value: string) {
    return (
      <PinPadButton
        key={value}
        value={value}
        onClick={handleClick}
        isDisabled={arePinButtonsDisabled}
        isPasscodeScreen={isPasscodeScreen}
        secondaryLabel={KEY_LETTERS[Number(value)]}
      />
    );
  }

  function renderBiometrics() {
    return !onBiometricsClick ? <span /> : (
      <PinPadButton
        onClick={onBiometricsClick}
        isDisabled={isPending}
        isPasscodeScreen={isPasscodeScreen}
        ariaLabel={lang(isFaceId ? 'Use Face ID' : 'enclave_use_biometrics')}
        className={isPasscodeScreen && styles.utilityButton}
      >
        <i className={buildClassName('icon', isFaceId ? 'icon-face-id' : 'icon-touch-id')} aria-hidden />
      </PinPadButton>
    );
  }

  function renderBackspace() {
    return (
      <Transition name="zoomFade" activeKey={shouldSuggestLogout && !value.length ? 0 : 1}>
        {onLogOutClick && shouldSuggestLogout && !value.length ? (
          <PinPadButton
            className={buildClassName(styles.buttonDanger, isPasscodeScreen && styles.utilityButton)}
            isPasscodeScreen={isPasscodeScreen}
            ariaLabel={lang('Exit all wallets')}
            onClick={onLogOutClick}
          >
            <i className="icon icon-exit" aria-hidden />
          </PinPadButton>
        ) : (
          <PinPadButton
            className={buildClassName(!canRenderBackspace && styles.buttonHidden,
              isPasscodeScreen && styles.utilityButton)}
            isPasscodeScreen={isPasscodeScreen}
            ariaLabel={lang('Delete last digit')}
            isDisabled={!canRenderBackspace || isPending}
            onClick={handleBackspaceClick}
          >
            <i className="icon icon-backspace" aria-hidden />
          </PinPadButton>
        )}
      </Transition>
    );
  }

  function renderDots() {
    const dotsClassName = buildClassName(
      styles.dots,
      type === 'error' && styles.dotsError,
      isLoading && styles.dotsLoading,
    );

    return (
      <div className={dotsClassName} aria-hidden>
        {Array.from({ length }, (_, i) => (
          <div
            key={i}
            className={buildClassName(
              styles.dot,
              i < value.length && styles.dotFilled,
              type === 'error' && styles.error,
              (isSuccess || isSuccessDuringAnimation) && styles.success,
            )}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={buildClassName(
        styles.root,
        IS_LEGENDS_WALLET && styles.rootLegends,
        isPasscodeScreen && styles.rootPasscode,
        isPasscodeScreen && isSuccess && styles.passcodeAccepted,
        IS_LEGENDS_WALLET && 'rr-block',
        className,
        SWIPE_DISABLED_CLASS_NAME,
      )}
      aria-busy={isLoading}
    >
      {isPasscodeScreen && renderDots()}
      {topContent ?? <div className={titleClassName} role="status" aria-live="polite">{title}</div>}
      {!isPasscodeScreen && renderDots()}

      <div className={buildClassName(styles.grid, isMinified && styles.minified)}>
        {Array.from({ length: 9 }, (_, i) => renderNumber(String(i + 1)))}
        {isPasscodeScreen ? renderBackspace() : renderBiometrics()}
        {renderNumber('0')}
        {isPasscodeScreen ? renderBiometrics() : renderBackspace()}
      </div>
      {footer}
    </div>
  );
}

export default memo(withGlobal<OwnProps>(
  (global) => {
    const { isPinAccepted } = global;

    return {
      isPinAccepted,
    };
  },
)(PinPad));
